<script lang="ts" context="module">
  import { z } from 'zod';
  import { ButtonTheme } from '$components/Button/BlockButton.svelte';

  export const CareersThemeSchema = z.object({
    button: ButtonTheme.optional(),
    buMarquee: BUMarqueeThemeSchema.optional(),
    brandColor: z.string(),
    jobContent: JobContentSchema,
    jobLocation: JobLocationTheme
  });

  export type CareersTheme = z.infer<typeof CareersThemeSchema>;

  const makeDarkTheme = (brandColor: string): CareersTheme => {
    return {
      button: {
        primaryButton: {
          default: { backgroundColor: brandColor, foregroundColor: 'black' }
        },
        secondaryButton: {
          default: {
            backgroundColor: '#fff',
            foregroundColor: '#000',
            borderColor: brandColor
          }
        }
      },
      brandColor,
      buMarquee: { brandColor },
      jobContent: { brandColor },
      jobLocation: { color: 'white' }
    };
  };

  const makeLightTheme = (brandColor: string): CareersTheme => {
    return {
      button: {
        primaryButton: {
          default: { backgroundColor: brandColor, foregroundColor: 'black' },
          hoverFocus: { backgroundColor: 'black', foregroundColor: 'white' },
          active: { backgroundColor: 'black', foregroundColor: 'white' }
        },
        secondaryButton: {
          default: {
            backgroundColor: '#fff',
            foregroundColor: '#000',
            borderColor: brandColor
          },
          hoverFocus: {
            backgroundColor: brandColor,
            foregroundColor: 'black',
            borderColor: brandColor
          },
          active: {
            backgroundColor: brandColor,
            foregroundColor: 'black',
            borderColor: brandColor
          }
        }
      },
      brandColor,
      buMarquee: { brandColor },
      jobContent: { brandColor },
      jobLocation: { color: 'black' }
    };
  };

  export const BU_CAREERS_THEMES: Record<string, { light: CareersTheme; dark: CareersTheme }> = {
    block: {
      dark: makeDarkTheme('#FF4F00'),
      light: makeLightTheme('#FF4F00')
    },
    square: {
      dark: makeDarkTheme('#006AFF'),
      light: makeLightTheme('#006AFF')
    },
    cashapp: {
      dark: makeDarkTheme('#00D533'),
      light: makeLightTheme('#00D533')
    },
    spiral: {
      dark: makeDarkTheme('#FF00FF'),
      light: makeLightTheme('#FF00FF')
    },
    tidal: {
      dark: makeDarkTheme('#0EC7C7'),
      light: makeLightTheme('#0EC7C7')
    },
    proto: {
      dark: makeDarkTheme('#FF5B00'),
      light: makeLightTheme('#FF5B00')
    },
    afterpay: {
      dark: makeDarkTheme('#B2FCE4'),
      light: makeLightTheme('#B2FCE4')
    }
  };

  export const getBUTheme = (bu: string, colorScheme: 'light' | 'dark' = 'dark') => {
    return BU_CAREERS_THEMES[bu][colorScheme];
  };
</script>

<script lang="ts">
  import { themeStyleVars } from '$lib/campaigns/theme';
  import { BUMarqueeThemeSchema } from './BUMarquee.svelte';
  import { JobContentSchema } from './JobContent.svelte';
  import { JobLocationTheme } from './JobLocation.svelte';

  export let theme: CareersTheme;
</script>

<div style={themeStyleVars(theme ?? {})}>
  <slot />
</div>
